import React, { useMemo, useState } from 'react';
import './LoginScreen.css';
import config from '../config';

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

export default function RegisterScreen({ onRegister, onSwitchToLogin }) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const canSubmit = useMemo(() => {
    return firstName.trim().length > 0 && isValidEmail(email) && password.trim().length >= 6 && !isSubmitting;
  }, [firstName, email, password, isSubmitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (firstName.trim().length === 0) {
      setError('First name is required.');
      return;
    }
    if (!isValidEmail(email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (password.trim().length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          password: password,
          firstName: firstName.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error ?? data?.message ?? 'Registration failed. Please try again.');
      }

      // If JWT is present, user is verified (legacy) - log in. Otherwise, verification email was sent.
      if (data.jwt) {
        localStorage.setItem('vt_dashboard_token', data.jwt);
        onRegister(data);
      } else {
        // Account created but email verification required - show success, stay on register screen
        setError(null);
        setSuccess('Account created! Please check your email and click the verification link to activate your account.');
      }
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vt-login">
      <div className="vt-loginShell">
        <div className="vt-loginBrand" aria-label="Veritage">
          <div className="vt-loginLogo" aria-hidden="true">
            <svg
              width="32"
              height="32"
              viewBox="0 0 200 200"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <g transform="translate(100,100)" stroke="currentColor" strokeWidth="5" strokeLinecap="round">
                <g>
                  <circle cx="0" cy="0" r="82" fill="none" stroke="none" />
                  {[...Array(20)].map((_, i) => (
                    <line
                      key={`outer-${i}`}
                      x1="0"
                      y1="-82"
                      x2="0"
                      y2="-70"
                      transform={`rotate(${i * 18})`}
                    />
                  ))}
                </g>
                <g>
                  <circle cx="0" cy="0" r="60" fill="none" stroke="none" />
                  {[...Array(20)].map((_, i) => (
                    <line
                      key={`inner-${i}`}
                      x1="0"
                      y1="-56"
                      x2="0"
                      y2="-48"
                      transform={`rotate(${i * 18})`}
                    />
                  ))}
                </g>
              </g>
            </svg>
          </div>
          <div className="vt-loginBrandText">
            <div className="vt-loginBrandName">Veritage</div>
            <div className="vt-loginBrandTag vt-muted">Enterprise portal</div>
          </div>
        </div>

        <div className="vt-loginCard vt-surface">
          <div className="vt-loginHeader">
            <h1 className="vt-loginTitle">Sign up</h1>
            <p className="vt-loginSubtitle vt-muted">
              Create a new account to access the dashboard.
            </p>
          </div>

          <form className="vt-loginForm" onSubmit={handleSubmit}>
            <label className="vt-field">
              <span className="vt-fieldLabel">First name</span>
              <input
                className="vt-input"
                type="text"
                autoComplete="given-name"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </label>

            <label className="vt-field">
              <span className="vt-fieldLabel">Email</span>
              <input
                className="vt-input"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>

            <label className="vt-field">
              <span className="vt-fieldLabel">Password</span>
              <input
                className="vt-input"
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>

            {error && (
              <div className="vt-loginError" role="alert">
                {error}
              </div>
            )}
            {success && (
              <div className="vt-loginSuccess" role="status" style={{ color: 'var(--vt-success, #0a7)', marginBottom: '1rem' }}>
                {success}
              </div>
            )}

            <button className="vt-btn vt-btn--primary vt-loginSubmit" type="submit" disabled={!canSubmit}>
              {isSubmitting ? 'Creating account…' : 'Sign up'}
            </button>

            <div className="vt-loginFooter">
              <button
                type="button"
                className="vt-link"
                onClick={onSwitchToLogin}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '13px',
                  padding: 0,
                }}
              >
                Already have an account? Sign in
              </button>
            </div>
          </form>
        </div>

        <div className="vt-loginMeta vt-muted">
          <span>© {new Date().getFullYear()} Veritage</span>
          <span className="vt-loginDot" aria-hidden="true">
            •
          </span>
          <span>Security & compliance by design</span>
        </div>
      </div>
    </div>
  );
}
